import React, { useState } from 'react';
import axios from 'axios';
import './RealizedGains.css';
import { API_BASE_URL } from '../lib/api';

function ClosedPositionForm({ position = null, onSuccess, onCancel }) {
  const isEdit = position !== null && position.transactionId !== undefined;

  const [formData, setFormData] = useState({
    statementType: position?.statementType || 'INDIVIDUAL',
    assetName: position?.assetName || '',
    ticker: position?.ticker || '',
    assetType: position?.assetType || 'Stock',
    quantitySold: position?.quantitySold || '',
    salePrice: position?.salePrice || '',
    saleDate: position?.date || new Date().toISOString().split('T')[0],
    purchasePrice: position?.purchasePrice || '',
    dateAcquired: position?.dateAcquired || '',
    platform: position?.platform || '',
    currency: position?.currency || 'USD',
    notes: position?.notes || '',
    totalRealizedGain: position?.realizedGain || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const ASSET_TYPES = ['Stock', 'ETF', 'Cryptocurrency', 'Precious Metal', 'Other'];
  const CURRENCIES = ['USD', 'EUR', 'GBP', 'SEK', 'NOK', 'DKK'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const isAnnual = formData.statementType === 'ANNUAL';

      // Validation based on statement type
      if (isAnnual) {
        // Annual statement validation
        if (!formData.platform || !formData.totalRealizedGain || !formData.saleDate) {
          throw new Error('Please fill in all required fields: Broker, Total Realized Gain, Sale Date');
        }

        if (isNaN(parseFloat(formData.totalRealizedGain))) {
          throw new Error('Total realized gain must be a number');
        }
      } else {
        // Individual position validation
        if (!formData.assetName || !formData.ticker || !formData.quantitySold ||
            !formData.salePrice || !formData.saleDate || !formData.purchasePrice ||
            !formData.dateAcquired) {
          throw new Error('Please fill in all required fields');
        }

        // Validate numeric values
        if (isNaN(parseFloat(formData.quantitySold)) || parseFloat(formData.quantitySold) <= 0) {
          throw new Error('Quantity must be a positive number');
        }

        if (isNaN(parseFloat(formData.salePrice)) || parseFloat(formData.salePrice) < 0) {
          throw new Error('Sale price must be a non-negative number');
        }

        if (isNaN(parseFloat(formData.purchasePrice)) || parseFloat(formData.purchasePrice) < 0) {
          throw new Error('Purchase price must be a non-negative number');
        }

        // Validate dates
        const saleDate = new Date(formData.saleDate);
        const acquiredDate = new Date(formData.dateAcquired);

        if (saleDate < acquiredDate) {
          throw new Error('Sale date must be after acquisition date');
        }
      }

      // Build payload based on statement type
      const payload = {
        statementType: formData.statementType,
        currency: formData.currency,
        saleDate: formData.saleDate,
        notes: formData.notes || '',
      };

      if (isAnnual) {
        payload.platform = formData.platform;
        payload.totalRealizedGain = parseFloat(formData.totalRealizedGain);
      } else {
        payload.assetName = formData.assetName;
        payload.ticker = formData.ticker;
        payload.assetType = formData.assetType;
        payload.quantitySold = parseFloat(formData.quantitySold);
        payload.salePrice = parseFloat(formData.salePrice);
        payload.purchasePrice = parseFloat(formData.purchasePrice);
        payload.dateAcquired = formData.dateAcquired;
        payload.platform = formData.platform || '';
      }

      if (isEdit) {
        await axios.put(
          `${API_BASE_URL}/transactions/closed-positions/${position.transactionId}`,
          payload,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/transactions/closed-positions`,
          payload,
          { withCredentials: true }
        );
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving closed position:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save closed position');
    } finally {
      setLoading(false);
    }
  };

  const isAnnual = formData.statementType === 'ANNUAL';

  return (
    <div className="modal-overlay">
      <div className="modal-content closed-position-form" role="dialog" aria-labelledby="modal-title">
        <div className="modal-header">
          <h2 id="modal-title">{isEdit ? 'Edit' : 'Add'} Closed Position</h2>
          <button onClick={onCancel} className="btn-close" disabled={loading}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          {/* Statement Type Selector */}
          <div className="statement-type-selector">
            <label>
              <input
                type="radio"
                name="statementType"
                value="INDIVIDUAL"
                checked={formData.statementType === 'INDIVIDUAL'}
                onChange={handleChange}
                disabled={loading}
              />
              Individual Position (per ticker)
            </label>
            <label>
              <input
                type="radio"
                name="statementType"
                value="ANNUAL"
                checked={formData.statementType === 'ANNUAL'}
                onChange={handleChange}
                disabled={loading}
              />
              Annual Statement (total from broker)
            </label>
          </div>

          <div className="form-grid">
            {/* Show individual position fields */}
            {!isAnnual && (
              <>
                <div className="form-row">
                  <label htmlFor="assetName">Asset Name *</label>
                  <input
                    type="text"
                    id="assetName"
                    name="assetName"
                    value={formData.assetName}
                    onChange={handleChange}
                    placeholder="e.g., Apple Inc."
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-row">
                  <label htmlFor="ticker">Ticker *</label>
                  <input
                    type="text"
                    id="ticker"
                    name="ticker"
                    value={formData.ticker}
                    onChange={handleChange}
                    placeholder="e.g., AAPL"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-row">
                  <label htmlFor="assetType">Asset Type *</label>
                  <select
                    id="assetType"
                    name="assetType"
                    value={formData.assetType}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  >
                    {ASSET_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <label htmlFor="quantitySold">Quantity Sold *</label>
                  <input
                    type="number"
                    id="quantitySold"
                    name="quantitySold"
                    value={formData.quantitySold}
                    onChange={handleChange}
                    step="0.01"
                    min="0.01"
                    placeholder="e.g., 50"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-row">
                  <label htmlFor="salePrice">Sale Price (per unit) *</label>
                  <input
                    type="number"
                    id="salePrice"
                    name="salePrice"
                    value={formData.salePrice}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    placeholder="e.g., 180.50"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-row">
                  <label htmlFor="purchasePrice">Purchase Price (per unit) *</label>
                  <input
                    type="number"
                    id="purchasePrice"
                    name="purchasePrice"
                    value={formData.purchasePrice}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    placeholder="e.g., 150.00"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-row">
                  <label htmlFor="dateAcquired">Date Acquired *</label>
                  <input
                    type="date"
                    id="dateAcquired"
                    name="dateAcquired"
                    value={formData.dateAcquired}
                    onChange={handleChange}
                    max={new Date().toISOString().split('T')[0]}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-row">
                  <label htmlFor="platform">Platform</label>
                  <input
                    type="text"
                    id="platform"
                    name="platform"
                    value={formData.platform}
                    onChange={handleChange}
                    placeholder="e.g., Interactive Brokers"
                    disabled={loading}
                  />
                </div>
              </>
            )}

            {/* Show annual statement fields */}
            {isAnnual && (
              <>
                <div className="form-row">
                  <label htmlFor="platform">Broker/Platform *</label>
                  <input
                    type="text"
                    id="platform"
                    name="platform"
                    value={formData.platform}
                    onChange={handleChange}
                    placeholder="e.g., Interactive Brokers, Trading 212"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-row">
                  <label htmlFor="totalRealizedGain">Total Realized Gain/Loss *</label>
                  <input
                    type="number"
                    id="totalRealizedGain"
                    name="totalRealizedGain"
                    value={formData.totalRealizedGain}
                    onChange={handleChange}
                    step="0.01"
                    placeholder="e.g., 5000.00 (use negative for loss)"
                    required
                    disabled={loading}
                  />
                </div>
              </>
            )}

            {/* Common fields */}
            <div className="form-row">
              <label htmlFor="saleDate">{isAnnual ? 'Year End Date *' : 'Sale Date *'}</label>
              <input
                type="date"
                id="saleDate"
                name="saleDate"
                value={formData.saleDate}
                onChange={handleChange}
                max={new Date().toISOString().split('T')[0]}
                required
                disabled={loading}
              />
            </div>

            <div className="form-row">
              <label htmlFor="currency">Currency *</label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                required
                disabled={loading}
              >
                {CURRENCIES.map(curr => (
                  <option key={curr} value={curr}>{curr}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row full-width">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Optional notes about this sale..."
              rows="3"
              disabled={loading}
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Saving...' : (isEdit ? 'Update' : 'Add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ClosedPositionForm;
